import { WorkflowTaskDetailDrawer } from "#batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskDetailDrawer";
import { WorkflowTaskUpdateAssigneeDialog } from "#src/batteries-included-components/Dialogs/WorkflowTaskUpdateAssigneeDialog";
import {
  FilterConfig,
  WorkflowTasksTableFilterArea,
  WorkflowTasksTableFiltersType,
  WorkflowTasksTableTitleDecorationFilterArea,
  type WorkflowTasksViewFiltersType,
} from "#src/batteries-included-components/FilterAreas/WorkflowTasksFilterAreas";
import { WorkflowTaskUserGroupDropdownGroups } from "#src/batteries-included-components/Filters/WorkflowTaskUserGroupDropdown/WorkflowTaskUserGroupDropdown";
import {
  WorkflowTaskPriorityDataDisplay,
  WorkflowTaskStatusToPillVariantMap,
} from "#src/batteries-included-components/Panels/TablePanels/WorkflowsTablePanel/WorkflowsTablePanel.helpers";
import { WorkflowStepStatusPill } from "#src/batteries-included-components/PillsCollection/WorkflowStepStatusPill";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { AssistantUserDataDisplay } from "#src/components/Common/AssistantUserDataDisplay";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { UserGroupDataDisplay } from "#src/components/Common/UserGroupDataDisplay/UserGroupDataDisplay";
import { useListUserGroups } from "#src/components/hooks/adapters/useUserGroups";
import { useGetOneWorkflow } from "#src/components/hooks/adapters/useWorkflows";
import {
  useListWorkflowActions,
  useListWorkflowTasks,
} from "#src/components/hooks/adapters/useWorkflowTasks";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import {
  getWorkflowTaskIconFromType,
  getWorkflowTaskNameFromType,
} from "#src/components/Workflows/WorkflowsHelpers";
import {
  useAuthenticatedContext,
  useHasPermission,
} from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { linkToAssetDetailPage } from "#src/utils/links";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import {
  useMutation,
  useQueries,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  CellArguments,
  DataTable,
  DataTablePanel,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Icon,
  ItemAction,
  Link,
  Pill,
  RichTextMarkdownDataDisplay,
  StorageKeys,
  TextDataDisplay,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  type GroupedByEntryType,
  Resources,
  SortDirection,
  UserGroupsAdapter,
  UserGroupType,
  UserReturnType,
  UsersAdapter,
  UserTaskType,
  WorkflowStatus,
  type WorkflowStatusType,
  WorkflowStepTypeType,
  WorkflowTaskAdapter,
  WorkflowTaskBaseSchema,
  WorkflowTaskStatus,
  type WorkflowTaskStatusType,
  WorkflowTaskType,
  WorkflowTaskTypes,
  WorkflowTaskTypesType,
} from "@validereinc/domain";
import {
  Assets,
  AssetTypeSelectionType,
} from "@validereinc/domain-controllers";
import {
  timeDurationToTimeUnits,
  toFlattenedObject,
  toTitleCaseString,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { isToday } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./WorkflowTasksTablePanel.module.scss";

const { AssetTypeSelection } = Assets.view;
const cx = classNames.bind(styles);

export type WorkflowTasksTablePanelProps = {
  isDisplayingMyTasks?: boolean;
  isDisplayingActions?: boolean;
  taskFilters?: Partial<WorkflowTaskType>;
  onRowClick?: (data: WorkflowTaskType) => void;
  onReassignTask?: (task: WorkflowTaskType) => void;
  /** turns off certain columns as they're duplicated in the workflow details */
  isWithinWorkflowDetail?: boolean;
  defaultAssetType?: AssetTypeSelectionType;
  /** When set to true, task navigation becomes disabled */
  disableTaskNavigation?: boolean;
  /**
   * When provided, actions and tasks are searched for this taskId,
   * and the sidebar will open automatically.
   */
  defaultTaskId?: string;
  /** A callback for when a task is selected */
  onTaskSelection?: (taskId?: string) => void;
} & StorageKeys;

export const WorkflowTasksTablePanel = ({
  isDisplayingMyTasks,
  isDisplayingActions,
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  groupByTablesStateStorageKey,
  taskFilters,
  onRowClick,
  onReassignTask,
  isWithinWorkflowDetail,
  defaultAssetType,
  disableTaskNavigation,
  defaultTaskId,
  onTaskSelection: setSelectedTaskCallback,
}: WorkflowTasksTablePanelProps) => {
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [viewFilters] = useStickyState<WorkflowTasksViewFiltersType>(
    {} as WorkflowTasksViewFiltersType,
    viewConfigStorageKey
  );
  const [tableFilters] = useStickyState<WorkflowTasksTableFiltersType>(
    {} as WorkflowTasksTableFiltersType,
    filterConfigStorageKey
  );
  const { assignee, templateId, ...applicableViewFilters } = viewFilters;
  const {
    name,
    status,
    workflow: workflowTableFilter,
    categoryId,
    step_type,
    type,
    ...applicableTableFilters
  } = tableFilters;
  const { due_date, ...applicableWorkflowTableFilters } =
    workflowTableFilter ?? {};

  const [selectedTask, setSelectedTaskState] =
    useState<WorkflowTaskType | null>(null);
  const [reassignTask, setReassignTask] = useState<WorkflowTaskType | null>(
    null
  );
  const [shouldPoll, setShouldPoll] = useState(false);
  const minDate = due_date?.from;
  const maxDate = due_date?.to;

  const setSelectedTask: typeof setSelectedTaskState = useCallback(
    (task) => {
      setSelectedTaskCallback?.(task && "id" in task ? task.id : undefined);
      setSelectedTaskState(task);
    },
    [setSelectedTaskState]
  );

  const assigneeType = assignee?.[0];
  const assigneeValue = assignee?.[1];
  const filters: Parameters<typeof WorkflowTaskAdapter.getList>[0]["filters"] =
    {
      name,
      ...((status
        ? {
            status: Array.isArray(status)
              ? (status as WorkflowTaskStatusType[])
              : { $exact: status as WorkflowTaskStatusType },
          }
        : {}) as any),
      ...getAssetFilters({
        ...toFlattenedObject({
          workflow: applicableWorkflowTableFilters,
        }),
        ...applicableTableFilters,
        ...applicableViewFilters,
      }),
      "workflow_category.id": categoryId,
      "workflow_template.id": templateId,
      $and: [
        taskFilters ?? {},
        ...((!isWithinWorkflowDetail
          ? [
              {
                ...(assigneeType ===
                  WorkflowTaskUserGroupDropdownGroups.USERS && assigneeValue
                  ? { assignee_user: assigneeValue }
                  : assigneeType ===
                        WorkflowTaskUserGroupDropdownGroups.GROUPS &&
                      assigneeValue
                    ? { assignee_group: assigneeValue }
                    : null),
                ...(minDate || maxDate
                  ? {
                      $or: [
                        {
                          $and: [
                            ...(minDate
                              ? [
                                  {
                                    [FilterConfig.dueDate.name]: {
                                      $gte: minDate,
                                    },
                                  } as any,
                                ]
                              : []),
                            ...(maxDate
                              ? [
                                  {
                                    [FilterConfig.dueDate.name]: {
                                      $lte: maxDate,
                                    },
                                  },
                                ]
                              : []),
                          ],
                        },
                      ],
                    }
                  : {}),
              },
            ]
          : [
              {
                ...(step_type
                  ? { step_type: step_type as WorkflowStepTypeType }
                  : {}),
                ...(type ? { type: type } : {}),
              },
            ]) as any),
      ],
    };

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const requestPayload: Parameters<
    typeof WorkflowTaskAdapter.actions.getList
  >[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: filters as any,
  };
  const {
    v2: {
      userInfo: { user },
    },
  } = useAuthenticatedContext();
  const [canReadUserGroups] = useHasPermission("user_groups:read");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { localize } = useLocalization();

  const { data: userGroupsData } = useListUserGroups(
    { filters: { "user.id": [user?.id] } },
    { enabled: !!user && canReadUserGroups }
  );
  const userGroups = userGroupsData?.data ?? [];

  const actionsQuery = useListWorkflowActions(requestPayload, {
    enabled: !!isDisplayingActions,
    refetchInterval: shouldPoll ? 4 * 1000 : false,
  });
  // @ts-expect-error TODO: Typing on filter
  const tasksQuery = useListWorkflowTasks(requestPayload, {
    enabled: !isDisplayingActions,
    refetchInterval: shouldPoll ? 4 * 1000 : false,
  });
  const { data, isLoading: isTasksLoading } = isDisplayingActions
    ? actionsQuery
    : tasksQuery;

  const items = data?.data ?? [];
  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...requestPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? requestPayload)
  );

  const workflowTasksGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [Resources.WORKFLOW_TASK, "getList", d.payload],
      queryFn: () => WorkflowTaskAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const workflowId = data?.data?.[0]?.workflow_id ?? "";
  const workflowQuery = useGetOneWorkflow(
    { id: workflowId },
    { enabled: Boolean(workflowId) && !!isDisplayingActions }
  );

  const workflow = useMemo(() => workflowQuery.data?.data, [workflowQuery]);

  useEffect(() => {
    if (
      !!workflow?.status &&
      (
        [
          WorkflowStatus.IN_PROGRESS,
          WorkflowStatus.OVERDUE,
        ] as WorkflowStatusType[]
      ).includes(workflow?.status)
    ) {
      setShouldPoll(true);
    } else {
      return setShouldPoll(false);
    }
  }, [workflow?.status]);

  /**
   * An effect that listens to changes in `data` (list of actions),
   * and if finds a task/action with `defaultTaskId`, will select that task
   */
  useEffect(() => {
    if (!defaultTaskId || !data?.data) return;

    const foundTask = data.data.find((t) => t.id === defaultTaskId);

    setSelectedTask(foundTask ?? null);
  }, [data?.data, setSelectedTask, defaultTaskId]);

  const assignedToUserQueries = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries: tableState.groupBy.length
      ? workflowTasksGroupedByQueries
          .flatMap(
            (q) =>
              q.data?.data.map(({ assignee_user }) => assignee_user ?? "") ?? []
          )
          .map((userId) => {
            return {
              queryKey: ["users", userId],
              queryFn: () => {
                if (!userId) {
                  return;
                }

                return UsersAdapter.getOne({ id: userId });
              },
              enabled: Boolean(userId),
              select: (resp) => resp?.data,
            };
          })
      : (data?.data.map((task) => {
          return {
            queryKey: ["users", task.assignee_user],
            queryFn: () => {
              if (!task.assignee_user) {
                return;
              }

              return UsersAdapter.getOne({ id: task.assignee_user });
            },
            enabled: Boolean(task.assignee_user),
            select: (resp) => resp?.data,
          };
        }) ?? []),
  });
  const assignedToUserGroupQueries = useQueries({
    queries: tableState.groupBy.length
      ? workflowTasksGroupedByQueries
          .flatMap(
            (q) =>
              q.data?.data.map(({ assignee_group }) => assignee_group ?? "") ??
              []
          )
          .map((userGroupId) => {
            return {
              queryKey: ["users", "groups", userGroupId],
              queryFn: () => {
                if (!userGroupId) return;
                return UserGroupsAdapter.getOne({ id: userGroupId });
              },
              enabled: !!userGroupId && canReadUserGroups,
            };
          })
      : (data?.data.map((task) => {
          return {
            queryKey: ["users", "groups", task.assignee_group],
            queryFn: () => {
              if (!task.assignee_group) return;
              return UserGroupsAdapter.getOne({ id: task.assignee_group });
            },
            enabled: !!task.assignee_group && canReadUserGroups,
          };
        }) ?? []),
  });

  const { mutate: updateTask } = useMutation({
    mutationFn: (
      payload: Parameters<typeof WorkflowTaskAdapter.updateOne>[0]
    ) => WorkflowTaskAdapter.updateOne(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Resources.WORKFLOW],
      });

      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW],
        });
      }, 4000);
      toast.push({
        intent: "success",
        description: "Successfully updated task",
      });
    },
    onError: (err) => {
      console.error(err);
      toast.push({
        intent: "error",
        description: "Unable to update task",
      });
    },
  });

  const assignedToUserMap = useMemo(
    () =>
      assignedToUserQueries.reduce<Record<string, UserReturnType>>((map, q) => {
        if (!q.data) {
          return map;
        }

        map[q.data.id] = q.data;
        return map;
      }, {}),
    [assignedToUserQueries]
  );
  const assignedToUserGroupMap = useMemo(
    () =>
      assignedToUserGroupQueries.reduce<Record<string, UserGroupType>>(
        (map, q) => {
          if (!q.data) {
            return map;
          }

          map[q.data.data.id] = q.data.data;
          return map;
        },
        {}
      ),
    [assignedToUserGroupQueries]
  );

  const renderRemainingDelayTime = (item: WorkflowTaskType) => {
    const delayDate = Date.parse(item?.description);
    if (!isNaN(delayDate)) {
      const now = Date.now();
      const timeRemaining = delayDate - now;
      if (timeRemaining < 0) {
        return "Completed";
      } else {
        return `Time Remaining: ${timeDurationToTimeUnits(timeRemaining)
          .map((i) => `${i.value} ${i.unit}`)
          .join(", ")}`;
      }
    } else {
      return item?.description;
    }
  };

  const checkIfUserAssignedToTask = (task: WorkflowTaskType) => {
    const isUserInAssigneeGroup = userGroups.some(
      (userGroup) => userGroup.id === task?.assignee_group
    );
    const isUserAssignee = user?.id === task?.assignee_user;
    const isUserAssignedToTask = isUserAssignee || isUserInAssigneeGroup;

    return isUserAssignedToTask;
  };

  const renderAssignedTo = ({ item }: CellArguments<WorkflowTaskType>) => {
    if (item.assignee_group && assignedToUserGroupMap[item.assignee_group]) {
      return (
        <UserGroupDataDisplay
          id={item.assignee_group}
          name={assignedToUserGroupMap[item.assignee_group].name}
        />
      );
    } else if (item.assignee_user && assignedToUserMap[item.assignee_user]) {
      const name = assignedToUserMap[item.assignee_user]?.name ?? "";
      return (
        <UserDataDisplay
          id={item.assignee_user}
          name={name}
        />
      );
    } else if (item.step_type === "system_action") {
      return <AssistantUserDataDisplay sizeVariant="small" />;
    } else {
      return <>-</>;
    }
  };

  const onTaskClick = (item: WorkflowTaskType) => {
    setSelectedTask(item);
    onRowClick?.(item);
  };

  const actionsHeaders: Array<HeaderType<WorkflowTaskType>> = [
    {
      label: "Title",
      key: WorkflowTaskBaseSchema.keyof().Enum.name,
      renderComponent: ({ item }) => (
        <Link
          label={item?.name}
          onClick={() => {
            onTaskClick(item);
          }}
        />
      ),
    },
    {
      label: "Priority",
      key: WorkflowTaskBaseSchema.keyof().Enum.priority_id,
      isSortable: true,
      renderComponent: ({ item, isBusy, isLoading }) => (
        <WorkflowTaskPriorityDataDisplay
          priorityId={item.priority_id}
          priorityLabel={item.priority_label}
          isBusy={isBusy}
          isLoading={isLoading}
          shouldRenderNoneLabel={false}
        />
      ),
    },
    {
      label: "Description",
      key: WorkflowTaskBaseSchema.keyof().Enum.description,
      renderComponent: ({ item }) => {
        if (
          (
            [
              WorkflowTaskTypes.FORM_CHOICE,
              WorkflowTaskTypes.CREATE_EVENT,
            ] as WorkflowTaskTypesType[]
          ).includes(item?.type)
        ) {
          return <DataTable.DataRow.TextCell value={item?.entity_name} />;
        } else if (item?.type == "delay") {
          return (
            <DataTable.DataRow.TextCell
              value={renderRemainingDelayTime(item)}
            />
          );
        } else {
          return <RichTextMarkdownDataDisplay value={item?.description} />;
        }
      },
    },
    {
      label: "Status",
      key: WorkflowTaskBaseSchema.keyof().Enum.status,
      renderComponent: ({ item, isLoading }) => {
        if (!workflow || !data?.data) {
          return null;
        }

        return (
          <WorkflowStepStatusPill
            workflowStep={item}
            isLoading={isLoading}
            shouldShowIcon
          />
        );
      },
    },
    {
      label: "Due Date",
      key: "due_date",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.due_date}
          withTime={!!(item.due_date && isToday(new Date(item.due_date)))}
          convertToUTC={false}
        />
      ),
    },
    {
      label: "Action Type",
      key: WorkflowTaskBaseSchema.keyof().Enum.type,
      renderComponent: ({ item }) => (
        <div className={cx("action-type-label")}>
          <div
            className={cx({
              "action-type-icon-container": true,
              "is-color-teal": item.step_type === "system_action",
            })}
          >
            <Icon
              variant={getWorkflowTaskIconFromType(item.type)}
              size={20}
              weight={item.type === "ctr_consensus_choice" ? "fill" : undefined}
            />
          </div>
          <TextDataDisplay value={getWorkflowTaskNameFromType(item.type)} />
        </div>
      ),
    },
    {
      label: "Assigned to",
      key: WorkflowTaskBaseSchema.keyof().Enum.assignee_user,
      renderComponent: renderAssignedTo,
    },
    {
      label: "Created At",
      key: WorkflowTaskBaseSchema.keyof().Enum.created_at,
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          convertToUTC={false}
        />
      ),
    },
  ];

  const tasksHeaders: Array<HeaderType<WorkflowTaskType>> = [
    {
      label: "Title",
      key: WorkflowTaskBaseSchema.keyof().Enum.name,
      isSortable: true,
      renderComponent: ({ item }) => (
        <Link
          label={item?.name}
          onClick={() => {
            onTaskClick(item);
          }}
        />
      ),
    },
    {
      label: "Priority",
      key: WorkflowTaskBaseSchema.keyof().Enum.priority_id,
      isSortable: true,
      groupByKeys: ["priority_label", "priority_id"],
      groupBySortByKey: "priority_label",
      isGroupable: true,
      renderComponent: ({ item, isBusy, isLoading }) => (
        <WorkflowTaskPriorityDataDisplay
          priorityId={item.priority_id}
          priorityLabel={item.priority_label}
          isBusy={isBusy}
          isLoading={isLoading}
          shouldRenderNoneLabel={false}
        />
      ),
    },
    {
      label: "Status",
      key: WorkflowTaskBaseSchema.keyof().Enum.status,
      isSortable: true,
      isGroupable: true,
      renderComponent: ({ item }) => (
        <Pill
          variant={WorkflowTaskStatusToPillVariantMap[item.status] || "default"}
        >
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Description",
      key: WorkflowTaskBaseSchema.keyof().Enum.description,
      isSortable: true,
      renderComponent: ({ item }) => (
        <RichTextMarkdownDataDisplay
          value={item.description}
          isInline
        />
      ),
    },
    {
      label: "Assigned to",
      key: "assigned_to",
      isSortable: true,
      isGroupable: true,
      renderComponent: renderAssignedTo,
    },
    {
      label: "Task Type",
      key: WorkflowTaskBaseSchema.keyof().Enum.type,
      isSortable: true,
      isGroupable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell
          value={getWorkflowTaskNameFromType(item.type) ?? null}
        />
      ),
    },
    {
      label: "Task Due Date",
      key: WorkflowTaskBaseSchema.keyof().Enum.due_date,
      isSortable: true,
      isGroupable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.due_date}
          withTime={!!(item.due_date && isToday(new Date(item.due_date)))}
          convertToUTC={false}
        />
      ),
    },
    ...(!isWithinWorkflowDetail
      ? [
          {
            label: "Workflow",
            key: "workflow.name",
            isSortable: true,
            renderComponent: ({ item }: { item: WorkflowTaskType }) => (
              <RoutingLink
                to={WorkflowDetailsRoutePath.toLink({
                  pathParams: {
                    workflowId: item.workflow_id,
                  },
                })}
              >
                {item.workflow.name ?? item.workflow.workflow_template.name}
              </RoutingLink>
            ),
          },
          {
            label: "Workflow Due Date",
            key: "workflow.due_date",
            isSortable: true,
            renderComponent: ({ item }: { item: WorkflowTaskType }) => (
              <DataTable.DataRow.DateCell
                value={item.workflow.due_date}
                withTime={
                  !!(
                    item.workflow.due_date &&
                    isToday(new Date(item.workflow.due_date))
                  )
                }
                convertToUTC={false}
              />
            ),
          },
          {
            label: "Asset",
            isSortable: true,
            key: "workflow.asset.name",
            renderComponent: ({ item }: { item: WorkflowTaskType }) =>
              item.workflow.asset ? (
                <RoutingLink
                  to={linkToAssetDetailPage(
                    item.workflow.asset.asset_type,
                    item.workflow.asset.id
                  )}
                >
                  {item.workflow.asset.name}
                </RoutingLink>
              ) : (
                "-"
              ),
          },
          {
            label: "Asset Type",
            isSortable: false,
            key: "asset.type",
            renderComponent: ({ item }: { item: WorkflowTaskType }) => (
              <DataTable.DataRow.TextCell
                value={
                  item.workflow.asset
                    ? localize(item.workflow.asset.asset_type)
                    : null
                }
              />
            ),
          },
          {
            label: `Associated ${localize("facility")}`,
            key: "workflow.facility.id",
            isSortable: false,
            renderComponent: ({ item }: { item: WorkflowTaskType }) =>
              item.workflow.asset &&
              item.workflow.asset.asset_type !== AssetType.FACILITY &&
              item.workflow.facility ? (
                <RoutingLink
                  to={linkToFacilityDetail(item.workflow.facility.id)}
                >
                  {item.workflow.facility.name}
                </RoutingLink>
              ) : (
                "-"
              ),
          },
        ]
      : []),
    {
      key: WorkflowTaskBaseSchema.keyof().Enum.created_at,
      label: "Created At",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          convertToUTC={false}
        />
      ),
    },
  ];

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: workflowTasksGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers: tasksHeaders,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Workflow Tasks list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  const taskStepIds = data?.data.map((task) => task.step_id);

  const isLoading =
    isTasksLoading ||
    assignedToUserQueries.some((q) => q.isFetching && !q.isRefetching) ||
    assignedToUserGroupQueries.some((q) => q.isFetching && !q.isRefetching);

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: `${isDisplayingActions ? "Actions" : isDisplayingMyTasks ? "My Tasks" : "All Tasks"}`,
          titleDecorator: (
            <WorkflowTasksTableTitleDecorationFilterArea
              key={filterConfigStorageKey}
              filterConfigStorageKey={filterConfigStorageKey}
              isDisplayingMyTasks={Boolean(isDisplayingMyTasks)}
              isWithinAWorkflow={Boolean(isWithinWorkflowDetail)}
            />
          ),
        }}
        filterComponent={
          <WorkflowTasksTableFilterArea
            key={filterConfigStorageKey}
            viewConfigStorageKey={viewConfigStorageKey}
            filterConfigStorageKey={filterConfigStorageKey}
            defaultAssetType={defaultAssetType}
            isWithinAWorkflow={isWithinWorkflowDetail}
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          intent: "simplicity-first",
          showRowNumbers: isWithinWorkflowDetail,
          rowNumberDirection:
            tableState.sortDirection === "desc" ? "reverse" : "forward",
          headers: isDisplayingActions ? actionsHeaders : tasksHeaders,
          items: data?.data ?? [],
          getItemId: (item) => item.id,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          isLoading,
          isBusy:
            (tasksQuery.isFetching || actionsQuery.isFetching) && !shouldPoll,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions: ({
            item,
          }: {
            item: WorkflowTaskType;
          }): ItemAction[] =>
            item.status === WorkflowTaskStatus.OPEN ||
            item.status === WorkflowTaskStatus.OVERDUE
              ? [
                  ...(item.type !== UserTaskType.CHOICE
                    ? ([
                        {
                          label: `${
                            ((item?.restrict_assignee_completion &&
                              !checkIfUserAssignedToTask(item)) ??
                            false)
                              ? "Only assignees can action this task"
                              : "Complete"
                          }`,
                          buttonProps: {
                            icon: "check-circle",
                            disabled:
                              (item?.restrict_assignee_completion &&
                                !checkIfUserAssignedToTask(item)) ??
                              false,
                            onClick: () =>
                              updateTask({
                                id: item.id,
                                data: {
                                  status: WorkflowTaskStatus.COMPLETE,
                                },
                                previousData: item,
                              }),
                          },
                        },
                      ] satisfies ItemAction[])
                    : []),
                  ...(item.is_dismissible
                    ? ([
                        {
                          label: "Dismiss",
                          buttonProps: {
                            icon: "x-circle",
                            onClick: () =>
                              updateTask({
                                id: item.id,
                                data: {
                                  status: WorkflowTaskStatus.DISMISSED,
                                },
                                previousData: item,
                              }),
                          },
                        },
                      ] satisfies ItemAction[])
                    : []),
                  {
                    label: "Reassign",
                    buttonProps: {
                      icon: "user-circle-gear",
                      onClick: () => {
                        setReassignTask(item);
                        onReassignTask?.(item);
                      },
                    },
                  },
                ]
              : [],
        }}
        {...groupByDataTableProps}
      />
      <WorkflowTaskDetailDrawer
        setSelectedTask={setSelectedTask}
        selectedTask={selectedTask}
        onReassignTask={(task) => setReassignTask(task)}
        taskStepIds={taskStepIds ?? []}
        disableTaskNavigation={disableTaskNavigation}
        shouldDisplayWorkflowTriggers
      />
      <WorkflowTaskUpdateAssigneeDialog
        isOpen={Boolean(reassignTask)}
        task={reassignTask}
        onClose={() => setReassignTask(null)}
      />
    </>
  );
};

function getAssetFilters({
  assetType,
  facilityId,
  equipmentId,
  deviceId,
  flowId,
  assetGroupId,
  workflow,
  ...rest
}: Partial<WorkflowTasksTableFiltersType> &
  Partial<WorkflowTasksViewFiltersType>) {
  const isValidId = (assetIds: string[] | undefined) => !!assetIds?.length;

  switch (assetType) {
    case AssetTypeSelection.FACILITY:
      return {
        ...rest,
        ...(isValidId(facilityId)
          ? { "workflow.facility.id": facilityId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.facility
          ? toFlattenedObject(workflow.facility, {
              prefix: "workflow.facility",
            })
          : {}),
      };
    case AssetTypeSelection.EQUIPMENT:
      return {
        ...rest,
        ...(isValidId(equipmentId)
          ? { "workflow.equipment.id": equipmentId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.equipment
          ? toFlattenedObject(workflow.equipment, {
              prefix: "workflow.equipment",
            })
          : {}),
      };
    case AssetTypeSelection.DEVICE:
      return {
        ...rest,
        ...(isValidId(deviceId)
          ? { "workflow.device.id": deviceId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.device
          ? toFlattenedObject(workflow.device, { prefix: "workflow.device" })
          : {}),
      };
    case AssetTypeSelection.FLOW:
      return {
        ...rest,
        ...(isValidId(flowId)
          ? { "workflow.flow.id": flowId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.flow
          ? toFlattenedObject(workflow.flow, { prefix: "workflow.flow" })
          : {}),
      };
    case AssetTypeSelection.ASSET_GROUP:
      return {
        ...rest,
        ...(isValidId(assetGroupId)
          ? { "workflow.asset_group.id": assetGroupId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.asset_group
          ? toFlattenedObject(workflow.asset_group, {
              prefix: "workflow.asset_group",
            })
          : {}),
      };
    default:
      return {};
  }
}
